<div class="mx-auto max-w-(--breakpoint-2xl) px-3">
  <div class="w-full">
    <div class="my-10 text-center">
      <h2 class="text-2xl font-medium text-gray-800 dark:text-white/90">
        @if (user.id) {
          Edit User
        } @else {
          Add User
        }
      </h2>
    </div>
  </div>

  <form [formGroup]="form">
    @if (archivedConflict) {
      <app-alert type="warning" class="mb-6 block">
        <div class="space-y-4">
          <div>
            <p class="font-medium">{{ archivedConflict.message }}</p>
            <p class="mt-1 text-sm">
              The current form matches an archived user record. Reactivate that record instead of creating a duplicate.
            </p>
          </div>

          <div class="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <span class="font-medium">Archived user:</span> {{ archivedConflict.archivedUser.name }}
            </div>
            <div>
              <span class="font-medium">Matched field:</span> {{ archivedConflict.matchedField }}
            </div>
            <div>
              <span class="font-medium">Email:</span> {{ archivedConflict.archivedUser.email }}
            </div>
            <div>
              <span class="font-medium">Roles:</span> {{ archivedConflictRoles }}
            </div>
            <div class="sm:col-span-2">
              <span class="font-medium">Archived at:</span> {{ archivedConflict.archivedUser.deleted_at }}
            </div>
          </div>

          <div class="flex flex-wrap gap-3">
            <app-button
              [label]="'Reactivate Archived User'"
              [style]="'primary'"
              [disabled]="processing || form.invalid"
              (click)="reactivateArchivedUser()"
            ></app-button>

            <app-button
              [label]="'Dismiss'"
              [style]="'secondary'"
              [disabled]="processing"
              (click)="dismissArchivedConflict()"
            ></app-button>
          </div>
        </div>
      </app-alert>
    }

    <div class="flex gap-3 items-start">
      <div class="w-full">

        <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">

          <div class="px-5 py-4 sm:px-6 sm:py-5">
            <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
              User Information
            </h3>
          </div>
          <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <app-text-input [control]="form.controls.name" [name]="'name'" [placeholder]="'User Name'" [label]="'Name'"></app-text-input>
              </div>
              <div>
                <app-text-input [control]="form.controls.email" [type]="'email'" [name]="'email'" [placeholder]="'user@example.com'" [label]="'Email'"></app-text-input>
              </div>
              <div>
                <app-select [control]="form.controls.role" [height]="'11'" [label]="'Role'" [choices]="roleChoices"></app-select>
              </div>
              <div>
                <app-select [multiple]="true" [control]="form.controls.venues" [choices]="venueChoices" [placeholder]="'Select venues...'" label="Venues"></app-select>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <app-text-input [control]="form.controls.password" [type]="'password'" [name]="'password'" [placeholder]="'***'" [label]="'Password'"></app-text-input>
              </div>

              @if (showPasswordRequirements) {
                <div class="rounded-lg bg-gray-50 dark:bg-gray-900/30 p-3 border border-gray-200 dark:border-gray-700">
                  <p class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Password Requirements:</p>
                  <ul class="space-y-2">
                    <li class="flex items-center gap-2 text-xs">
                      @if (passwordRequirements.minLength) {
                        <lucide-angular [img]="CheckIcon" [size]="14" class="text-green-600 dark:text-green-400 flex-shrink-0"></lucide-angular>
                      } @else {
                        <lucide-angular [img]="XCircleIcon" [size]="14" class="text-red-600 dark:text-red-400 flex-shrink-0"></lucide-angular>
                      }
                      <span [class.text-green-700]="passwordRequirements.minLength" [class.dark:text-green-300]="passwordRequirements.minLength" [class.text-gray-600]="!passwordRequirements.minLength" [class.dark:text-gray-400]="!passwordRequirements.minLength">
                        At least 8 characters
                      </span>
                    </li>
                    <li class="flex items-center gap-2 text-xs">
                      @if (passwordRequirements.hasUpperCase) {
                        <lucide-angular [img]="CheckIcon" [size]="14" class="text-green-600 dark:text-green-400 flex-shrink-0"></lucide-angular>
                      } @else {
                        <lucide-angular [img]="XCircleIcon" [size]="14" class="text-red-600 dark:text-red-400 flex-shrink-0"></lucide-angular>
                      }
                      <span [class.text-green-700]="passwordRequirements.hasUpperCase" [class.dark:text-green-300]="passwordRequirements.hasUpperCase" [class.text-gray-600]="!passwordRequirements.hasUpperCase" [class.dark:text-gray-400]="!passwordRequirements.hasUpperCase">
                        One uppercase letter (A-Z)
                      </span>
                    </li>
                    <li class="flex items-center gap-2 text-xs">
                      @if (passwordRequirements.hasLowerCase) {
                        <lucide-angular [img]="CheckIcon" [size]="14" class="text-green-600 dark:text-green-400 flex-shrink-0"></lucide-angular>
                      } @else {
                        <lucide-angular [img]="XCircleIcon" [size]="14" class="text-red-600 dark:text-red-400 flex-shrink-0"></lucide-angular>
                      }
                      <span [class.text-green-700]="passwordRequirements.hasLowerCase" [class.dark:text-green-300]="passwordRequirements.hasLowerCase" [class.text-gray-600]="!passwordRequirements.hasLowerCase" [class.dark:text-gray-400]="!passwordRequirements.hasLowerCase">
                        One lowercase letter (a-z)
                      </span>
                    </li>
                    <li class="flex items-center gap-2 text-xs">
                      @if (passwordRequirements.hasNumber) {
                        <lucide-angular [img]="CheckIcon" [size]="14" class="text-green-600 dark:text-green-400 flex-shrink-0"></lucide-angular>
                      } @else {
                        <lucide-angular [img]="XCircleIcon" [size]="14" class="text-red-600 dark:text-red-400 flex-shrink-0"></lucide-angular>
                      }
                      <span [class.text-green-700]="passwordRequirements.hasNumber" [class.dark:text-green-300]="passwordRequirements.hasNumber" [class.text-gray-600]="!passwordRequirements.hasNumber" [class.dark:text-gray-400]="!passwordRequirements.hasNumber">
                        One number (0-9)
                      </span>
                    </li>
                    <li class="flex items-center gap-2 text-xs">
                      @if (passwordRequirements.hasSpecialChar) {
                        <lucide-angular [img]="CheckIcon" [size]="14" class="text-green-600 dark:text-green-400 flex-shrink-0"></lucide-angular>
                      } @else {
                        <lucide-angular [img]="XCircleIcon" [size]="14" class="text-red-600 dark:text-red-400 flex-shrink-0"></lucide-angular>
                      }
                      <span [class.text-green-700]="passwordRequirements.hasSpecialChar" [class.dark:text-green-300]="passwordRequirements.hasSpecialChar" [class.text-gray-600]="!passwordRequirements.hasSpecialChar" [class.dark:text-gray-400]="!passwordRequirements.hasSpecialChar">
                        One special character (!&#64;#$%^&*)
                      </span>
                    </li>
                  </ul>
                </div>
              }
            </div>
          </div>
        </div>

      </div>

    </div>


    <div class="mt-8 flex flex-row items-center justify-between gap-3">
      <div class="flex gap-3">
        @if (canArchive) {
          <app-button
            [label]="'Archive User'"
            [icon]="ArchiveIcon"
            [style]="'danger'"
            [disabled]="processing"
            (click)="archiveUser()"
          ></app-button>
        }

        @if (canRestore) {
          <app-button
            [label]="'Restore User'"
            [icon]="RestoreIcon"
            [style]="'success'"
            [disabled]="processing"
            (click)="restoreUser()"
          ></app-button>
        }
      </div>

      <div class="flex gap-3">
        <app-button
          [label]="'Cancel'"
          [icon]="XIcon"
          [style]="'secondary'"
          [disabled]="processing"
          (click)="cancel()"
        ></app-button>

        <app-button
          [label]="'Save & Continue'"
          [icon]="CheckCircleIcon"
          [style]="'secondary'"
          [disabled]="processing || !form.valid || form.pristine"
          (click)="saveAndContinue()"
        ></app-button>

        <app-button
          [label]="'Save'"
          [icon]="CheckCircleIcon"
          [style]="'primary'"
          [disabled]="processing || !form.valid || form.pristine"
          (click)="save()"
        ></app-button>
      </div>
    </div>

<!--    @if (validationErrors.length > 0) {-->
<!--      <div class="mt-4 p-4 border border-red-500 bg-red-100 text-red-700 rounded-lg">-->
<!--        <h3 class="font-medium">Validation Errors:</h3>-->
<!--        <ul class="list-disc list-inside">-->
<!--          @for (error of validationErrors; track error) {-->
<!--            <li>{{ error }}</li>-->
<!--          }-->
<!--        </ul>-->
<!--      </div>-->
<!--    }-->

  </form>
</div>
